import React, { useEffect, useState, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
// leaflet map for location view
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import API_BASE_URL from '../config';

export default function LogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState(0);

  // swipe logs state (for second tab)
  const [swipes, setSwipes] = useState([]);
  const [swipeLoading, setSwipeLoading] = useState(true);
  const [swipeError, setSwipeError] = useState('');
  const [swipePage, setSwipePage] = useState(0);
  const [swipeRowsPerPage, setSwipeRowsPerPage] = useState(10);
  const [swipeQuery, setSwipeQuery] = useState('');
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationPos, setLocationPos] = useState(null);
  const [locationInfo, setLocationInfo] = useState('');
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [payloadData, setPayloadData] = useState(null);

  const showSwipePayload = (s) => {
    const parsed = parseSwipeRecord(s) || s.swipe_record || s;
    setPayloadData(parsed);
    setPayloadOpen(true);
  };

  // Format timestamp similar to Drivers page (Sri Lanka GMT+5:30)
  const formatSLTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const offsetDate = new Date(date.getTime() + (330 - date.getTimezoneOffset()) * 60000);
    return offsetDate.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }) + ' (GMT+5:30)';
  };

  // parse swipe_record which may be a JSON string or an object
  const parseSwipeRecord = (s) => {
    if (!s) return null;
    let record = s.swipe_record || s.swipe_data || s.record || s.data || s.payload || null;
    if (!record) return null;
    if (typeof record === 'object') return record;
    try {
      return JSON.parse(record);
    } catch (e) {
      // record might be a double-encoded JSON string; try to extract cardid via regex as fallback
      try {
        const m = String(record).match(/cardid\s*[:=]\s*"?([0-9A-Za-z-_]+)"?/i);
        if (m) return { cardid: m[1] };
      } catch (_) {}
      return null;
    }
  };

  const getLatLngFrom = (s, parsed) => {
    const pick = (obj, keys) => {
      if (!obj) return undefined;
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
      }
      return undefined;
    };
    const latKeys = ['lat','Latitude'];
    const lngKeys = ['lng','Longitude'];
    const lat = pick(parsed, latKeys) ?? pick(s, latKeys);
    const lng = pick(parsed, lngKeys) ?? pick(s, lngKeys);
    const nlat = lat !== undefined ? Number(lat) : NaN;
    const nlng = lng !== undefined ? Number(lng) : NaN;
    if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null;
    return { lat: nlat, lng: nlng };
  };

  // fix default icon path for leaflet in CRA / webpack contexts
  try {
    delete L.Icon.Default.prototype._getIconUrl;
    const iconRetina = require('leaflet/dist/images/marker-icon-2x.png');
    const icon = require('leaflet/dist/images/marker-icon.png');
    const shadow = require('leaflet/dist/images/marker-shadow.png');
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: iconRetina,
      iconUrl: icon,
      shadowUrl: shadow,
    });
  } catch (e) {
    // ignore in environments where require isn't available
  }

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/cron-logs`, {
          headers: {
            'Accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Failed to fetch logs');
        const data = await res.json();
        // Ensure array
        setLogs(Array.isArray(data) ? data : (data.logs || []));
      } catch (e) {
        setError(e.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  // fetch swipe logs for swipe log tab
  useEffect(() => {
    const fetchSwipes = async () => {
      setSwipeLoading(true);
      setSwipeError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/swipe-logs`, {
          headers: {
            'Accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Failed to fetch swipe logs');
        const data = await res.json();
        setSwipes(Array.isArray(data) ? data : (data.swipes || data.items || []));
      } catch (e) {
        setSwipeError(e.message || 'Unknown error');
      } finally {
        setSwipeLoading(false);
      }
    };
    fetchSwipes();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return logs;
    const q = query.toLowerCase();
    return logs.filter(l => {
      return (
    String(l.swipe_data.cardid || l.index || '').toLowerCase().includes(q) ||
    String(l.function_name || '').toLowerCase().includes(q) ||
    String(l.description || l.message || l.msg || '').toLowerCase().includes(q) ||
    String(l.execution_time || l.created_at || l.updated_at || '').toLowerCase().includes(q)
      );
    });
  }, [logs, query]);

  const filteredSwipes = useMemo(() => {
    if (!swipeQuery) return swipes;
    const q = swipeQuery.toLowerCase();
    return swipes.filter(s => {
      const parsed = parseSwipeRecord(s) || {};
      const card = String(parsed.DriverCardNo || parsed.cardid || parsed.card_id || s.cardid || s.card_id || parsed.license || '').toLowerCase();
      const name = String(parsed.DriverName || parsed.name || parsed.driver || s.driver_name || s.name || '').toLowerCase();
      const vehicle = String(parsed.VehicleId || parsed.carlicence || parsed.car_license || parsed.vehicle || s.vehicle || '').toLowerCase();
      const other = String(s.id || s.swipe_index || s.timestamp || s.created_at || '').toLowerCase();
      return card.includes(q) || name.includes(q) || vehicle.includes(q) || other.includes(q);
    });
  }, [swipes, swipeQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset to first page when query changes so users see results from start
  useEffect(() => {
    setPage(0);
  }, [query]);

  useEffect(() => { setSwipePage(0); }, [swipeQuery]);

  const handleTabChange = (e, v) => { setTab(v); };

  return (
    <div>
      <Typography variant="h4" gutterBottom>Logs</Typography>

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Logs" />
        <Tab label="Swipe Log" />
      </Tabs>

      {tab === 0 && (
        <>
          {loading && <Typography>Loading...</Typography>}
          {error && <Typography color="error">{error}</Typography>}

          {!loading && !error && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <TextField label="Search" value={query} onChange={(e) => setQuery(e.target.value)} size="small" />
              </Box>

              <TableContainer component={Paper} sx={{ mt: 1, overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log, idx) => (
                      <TableRow key={idx}>
                          <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{log.function_name || ''}</TableCell>
                        <TableCell style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.description || ''}</TableCell>
                        <TableCell>{(log.created_at || '')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5,10,25,50]}
                />
              </TableContainer>
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {swipeLoading && <Typography>Loading swipe logs...</Typography>}
          {swipeError && <Typography color="error">{swipeError}</Typography>}

          {!swipeLoading && !swipeError && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <TextField label="Search Swipe Logs" value={swipeQuery} onChange={(e) => setSwipeQuery(e.target.value)} size="small" />
              </Box>

              <TableContainer component={Paper} sx={{ mt: 1, overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Card ID</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Driver</TableCell>
                      <TableCell>Linde Response</TableCell>
                      <TableCell>View Location</TableCell>
                      <TableCell>Payload</TableCell>
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSwipes.slice(swipePage * swipeRowsPerPage, swipePage * swipeRowsPerPage + swipeRowsPerPage).map((s, idx) => {
                      const parsed = parseSwipeRecord(s) || {};
                      const pos = getLatLngFrom(s, parsed);
                      return (
                        <TableRow key={idx}>
                          <TableCell>{swipePage * swipeRowsPerPage + idx + 1}</TableCell>
                          <TableCell>{parsed.DriverCardNo || parsed.cardid || s.cardid || ''}</TableCell>
                          <TableCell>{parsed.VehicleId || parsed.carlicence || s.vehicle || ''}</TableCell>
                          <TableCell>{parsed.DriverName || parsed.name || s.driver_name || ''}</TableCell>
                          <TableCell>{s.linde_response || s.swipe_response || ''}</TableCell>
                          <TableCell>
                            <Button size="small" variant="outlined" onClick={() => {
                              if (pos) {
                                setLocationPos(pos);
                                setLocationInfo(`${parsed.DriverCardNo || parsed.cardid || ''} — ${parsed.DriverName || parsed.name || ''}`);
                                setLocationOpen(true);
                              } else {
                                setLocationPos(null);
                                setLocationInfo('No location available for this record');
                                setLocationOpen(true);
                              }
                            }}>
                              View
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => showSwipePayload(s)}>Show Payload</Button>
                          </TableCell>
                          <TableCell>{parsed.SwipeDateTime || s.swipe_datetime || s.created_at || ''}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredSwipes.length}
                  page={swipePage}
                  onPageChange={(e, p) => setSwipePage(p)}
                  rowsPerPage={swipeRowsPerPage}
                  onRowsPerPageChange={(e) => { setSwipeRowsPerPage(parseInt(e.target.value,10)); setSwipePage(0); }}
                  rowsPerPageOptions={[5,10,25,50]}
                />
              </TableContainer>

              <Modal open={locationOpen} onClose={() => setLocationOpen(false)}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 24, width: { xs: '95%', sm: 600 }, height: { xs: '60%', sm: 480 } }}>
                  <Typography variant="h6" gutterBottom>{locationInfo}</Typography>
                  {locationPos ? (
                    <Box sx={{ height: '100%' }}>
                      <MapContainer center={[locationPos.lat, locationPos.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[locationPos.lat, locationPos.lng]}>
                          <Popup>{locationInfo}</Popup>
                        </Marker>
                      </MapContainer>
                    </Box>
                  ) : (
                    <Typography>{locationInfo}</Typography>
                  )}
                </Box>
              </Modal>
              <Modal open={payloadOpen} onClose={() => setPayloadOpen(false)}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 24, width: { xs: '95%', sm: '80%', md: '70%' }, maxHeight: '85vh', overflowY: 'auto' }}>
                  <Typography variant="h6" gutterBottom>Swipe Payload</Typography>
                  {payloadData ? (
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{typeof payloadData === 'string' ? (() => {
                      try { const obj = JSON.parse(payloadData); return JSON.stringify(obj, null, 2); } catch (e) { return payloadData; }
                    })() : JSON.stringify(payloadData, null, 2)}</pre>
                  ) : (
                    <Typography>No payload data available</Typography>
                  )}
                </Box>
              </Modal>
            </>
          )}
        </>
      )}
    </div>
  );
}
